import type { ReportExplorerConfiguration } from './model';
import { SavedReportSource } from '../savedReports/model';
import { ReportDateRangeType, ReportDimension, ReportMetric } from './reportEnums';
import { createDefaultReportExplorerConfiguration } from './reportRequest';

export interface ReportExplorerRouteSearch {
  dimensions?: string;
  metrics?: string;
  from?: string;
  to?: string;
  dateRange?: string;
  savedReportId?: string;
  savedReportSource?: SavedReportSource;
  u?: string;
}

export interface ReportExplorerInitialSelection {
  dimensions: readonly ReportDimension[];
  metrics: readonly ReportMetric[];
  range: ReportExplorerInitialRange;
}

export interface ReportExplorerInitialRange {
  dateFrom: string;
  dateTo: string;
  preset?: ReportDateRangeType | undefined;
}

export function reportExplorerRouteSearchSchema(
  search: Record<string, unknown>,
): ReportExplorerRouteSearch {
  return {
    ...stringSearchValue(search['dimensions'], 'dimensions'),
    ...stringSearchValue(search['metrics'], 'metrics'),
    ...stringSearchValue(search['from'], 'from'),
    ...stringSearchValue(search['to'], 'to'),
    ...stringSearchValue(search['dateRange'], 'dateRange'),
    ...stringSearchValue(search['savedReportId'], 'savedReportId'),
    ...savedReportSourceSearchValue(search['savedReportSource']),
    ...stringSearchValue(search['u'], 'u'),
  };
}

function savedReportSourceSearchValue(value: unknown) {
  return value === SavedReportSource.Owned || value === SavedReportSource.Shared
    ? { savedReportSource: value }
    : {};
}

export function parseReportExplorerRouteSearch(
  search: ReportExplorerRouteSearch,
  now = new Date(),
  defaults = createDefaultReportExplorerConfiguration(now),
): ReportExplorerConfiguration {
  const dimensions = parseEnumList(search.dimensions, ReportDimension);
  const metrics = parseEnumList(search.metrics, ReportMetric);
  const range = parseRange(search);

  return {
    ...defaults,
    ...(dimensions.length > 0 ? { tableDimensions: dimensions } : {}),
    ...(metrics.length > 0 ? { tableMetrics: metrics } : {}),
    ...(range ?? {}),
  };
}

export function serializeReportExplorerInitialSelection(
  selection: ReportExplorerInitialSelection,
): ReportExplorerRouteSearch {
  const dateRange = readEnumValue(selection.range.preset, ReportDateRangeType);
  return {
    dimensions: selection.dimensions.join(','),
    metrics: selection.metrics.join(','),
    from: selection.range.dateFrom,
    to: selection.range.dateTo,
    ...(dateRange ? { dateRange } : {}),
  };
}

function parseRange(search: ReportExplorerRouteSearch) {
  if (!isIsoDate(search.from) || !isIsoDate(search.to) || search.from > search.to) return null;
  return {
    dateFrom: search.from,
    dateTo: search.to,
    dateRange: readEnumValue(search.dateRange, ReportDateRangeType) ?? null,
  };
}

function parseEnumList<TValue extends string>(
  value: string | undefined,
  enumObject: Record<string, TValue>,
) {
  if (!value) return [];
  const allowed = new Set<TValue>(Object.values(enumObject));
  return [
    ...new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter((item): item is TValue => allowed.has(item as TValue)),
    ),
  ];
}

function readEnumValue<TValue extends string>(
  value: string | undefined,
  enumObject: Record<string, TValue>,
) {
  return Object.values(enumObject).find((candidate) => candidate === value);
}

function isIsoDate(value: string | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function stringSearchValue<TKey extends keyof ReportExplorerRouteSearch>(
  value: unknown,
  key: TKey,
): Pick<ReportExplorerRouteSearch, TKey> | Record<never, never> {
  return typeof value === 'string' ? { [key]: value } : {};
}
